import { Guess } from '@wt/app/game/_components/types';
import { getUpdatedStats } from '@wt/app/game/_utils/getUpdatedStats';
import { loadAdScript } from '@wt/shared/hooks/useAds/useAds';
import {
  getIsLoggedIn,
  getUser,
  refreshTokens,
  syncStats,
  updateSeenAccountsUpdateModal,
} from '@wt/utilities/database';
import { nullFunc } from '@wt/utilities/nullFunction';
import { useRouter } from 'next/navigation';
import { toast } from 'react-toastify';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  convertDbUserToUser,
  DatabaseUser,
  getUserStats,
  initialUser,
  parseUserFromLocalStorage,
  User,
} from './types';
import {
  clearLocalStorage,
  isSameUtcDay,
  setLocalStorage,
  whenTakenUserLocalStorageKey,
} from './utils';

interface UserContextType {
  user: User;
  updateUser: (updatedUser: Partial<User>) => void;
  loggedIn: boolean;
  logOut: () => void;
  updateStats: (todaysGuesses: Guess[], dayString: string) => void;
  showLoginTooltip: boolean;
  setShowLoginToolip: (show: boolean) => void;
  showAccountsUpdateModal: boolean;
}

const initialContextValue: UserContextType = {
  user: initialUser,
  updateUser: nullFunc,
  loggedIn: false,
  logOut: nullFunc,
  updateStats: nullFunc,
  showLoginTooltip: false,
  setShowLoginToolip: nullFunc,
  showAccountsUpdateModal: false,
};

const UserContext = createContext<UserContextType>(initialContextValue);

export const useUserProvider = () => useContext(UserContext);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>(initialUser);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [showLoginTooltip, setShowLoginToolip] = useState<boolean>(false);
  const [showAccountsUpdateModal, setShowAccountsUpdateModal] = useState(false);
  const router = useRouter();

  useEffect(() => {
    async function loadUser() {
      const isLoggedIn = getIsLoggedIn();
      const encodedRefreshTime = localStorage.getItem('wt-refreshDate');
      const refreshTime = encodedRefreshTime
        ? decodeURIComponent(encodedRefreshTime)
        : null;

      // load user from local storage
      const userFromLocalStorage = localStorage.getItem(
        whenTakenUserLocalStorageKey
      );

      // set user to be local storage user
      // if logged in this will be overwritten by db data
      if (userFromLocalStorage) {
        setUser(parseUserFromLocalStorage(userFromLocalStorage));
      } else {
        // if user has not been set in local storage, create
        localStorage.setItem(
          whenTakenUserLocalStorageKey,
          JSON.stringify(initialUser)
        );
      }

      if (isLoggedIn) {
        try {
          setLoggedIn(true);
          if (refreshTime && !isSameUtcDay(refreshTime)) {
            await refreshTokens();
          }

          const dbUser: DatabaseUser = await getUser();

          // if first login, sync stats saved in local storage to db
          let didRedirect = false;
          if (dbUser.firstLogin) {
            if (userFromLocalStorage) {
              const parsedUser: User =
                parseUserFromLocalStorage(userFromLocalStorage);
              await syncStats(getUserStats(parsedUser));

              dbUser.gamesPlayed = parsedUser.gamesPlayed;
              dbUser.lastGamePlayed = parsedUser.lastGamePlayed;
              dbUser.currentStreak = parsedUser.currentStreak;
              dbUser.highestLocationScore = parsedUser.highestLocationScore;
              dbUser.highestYearScore = parsedUser.highestYearScore;
              dbUser.highestTotalScore = parsedUser.highestTotalScore;
              dbUser.totalLocationScore = parsedUser.totalLocationScore;
              dbUser.totalYearScore = parsedUser.totalYearScore;

              // Also redirect to the edit profile page as "Finish setting up your account"
              router.push('/account/edit#first-login');
              didRedirect = true;
            }
          }

          if (!didRedirect) {
            const postLoginLocation = localStorage.getItem(
              'post-login-location'
            );
            if (postLoginLocation) {
              localStorage.removeItem('post-login-location');
              router.push(postLoginLocation);
            }
          }

          // set guesses, archive guesses, and custom guesses from db data
          setLocalStorage(dbUser);

          updateUser(convertDbUserToUser(dbUser));

          loadAdScript(dbUser.isPremium);

          if (!dbUser.seenAccountsUpdateModal) {
            // checkToShowAccountsUpdateModal(true);
          }
        } catch (e) {
          logOut();
          toast.error('You have been logged out. Please log in again.', {
            position: 'top-center',
          });
        }
      } else {
        // if not logged in, load ads
        loadAdScript(false);
        // checkToShowAccountsUpdateModal(false);
      }
    }

    loadUser();
  }, []);

  const logOut = (): void => {
    updateUser(initialUser);
    setLoggedIn(false);
    clearLocalStorage();
    loadAdScript(false);
  };

  // updates user in provider and local storage
  const updateUser = (updatedUserInfo: Partial<User>): void => {
    const newUser: User = {
      ...user,
      ...updatedUserInfo,
    };

    setUser(newUser);
    localStorage.setItem(whenTakenUserLocalStorageKey, JSON.stringify(newUser));
  };

  const updateStats = (todaysGuesses: Guess[], dayString: string): void => {
    // don't update stats multiple times
    if (user.lastGamePlayed === dayString) return;

    const newStats = getUpdatedStats(
      getUserStats(user),
      todaysGuesses,
      dayString
    );

    updateUser(newStats);
    if (loggedIn) syncStats(newStats);
    return;
  };

  const checkToShowAccountsUpdateModal = async (isLoggedIn: boolean) => {
    const storedSeenModal = localStorage.getItem('accounts-update-modal');
    if (storedSeenModal !== 'true' && Date.now() < 1747348430000) {
      setShowAccountsUpdateModal(true);
      localStorage.setItem('accounts-update-modal', 'true');
      if (isLoggedIn) {
        updateSeenAccountsUpdateModal();
      }
    }
    return;
  };

  return (
    <UserContext.Provider
      value={{
        user,
        updateUser,
        loggedIn,
        logOut,
        updateStats,
        showLoginTooltip,
        setShowLoginToolip,
        showAccountsUpdateModal,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
